//@ts-nocheck
import React, { useEffect, useState } from "react";
import { DatePicker, Select, Skeleton } from "antd";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import {
  FiUsers, FiSend, FiClock, FiCheckCircle, FiPercent, FiDollarSign,
  FiAlertTriangle, FiPackage, FiShoppingCart, FiRefreshCw,
  FiBarChart2, FiFileText, FiTarget, FiLayers, FiCalendar, FiChevronDown, FiMail,
} from "react-icons/fi";
import { getAteDashboardSummary } from "../../utils/API";
import CommonHeader from "../../Components/commonHeader";
import PageLogoutButton from "../../Components/PageLogoutButton";
import "./ate-dashboard.css";

const PERIODS = [
  { value: "today", label: "Today" },
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const num = (v) => Number(v || 0);
const int = (v) => num(v).toLocaleString("en-US");
const usd = (v) => `$${num(v).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const usd2 = (v) => `$${num(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v, d = 2) => `${num(v).toFixed(d)}%`;
const compact = (v) => Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 2 }).format(num(v));

const AteDashboard: React.FC = () => {
  const [period, setPeriod] = useState("year");
  const [customRange, setCustomRange] = useState<[string, string] | null>(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    if (period === "custom" && !customRange) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getAteDashboardSummary(
      period === "custom"
        ? { period: "custom", startDate: customRange[0], endDate: customRange[1] }
        : { period },
    )
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) { setData(null); setError(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [period, customRange, reload]);

  const k = data?.kpis;
  const q = k?.quotations || {};

  const totalQuotes = num(q.sent) + num(q.pending) + num(q.approved) + num(q.notSentYet);
  const approvedShare = totalQuotes ? (num(q.approved) / totalQuotes) * 100 : 0;
  const pendingShare = totalQuotes ? (num(q.pending) / totalQuotes) * 100 : 0;
  const notSentShare = totalQuotes ? (num(q.notSentYet) / totalQuotes) * 100 : 0;

  // 9 KPI cards arranged in 2 logical groups (5 Leads & Quotations, 4 Sales & Revenue)
  const kpiGroups = [
    {
      title: "Leads & Quotations",
      gridClass: "ad-grid-5",
      cards: k
        ? [
            { label: "Total Leads", value: int(k.totalLeads), icon: <FiUsers />, color: "#004aad" },
            { label: "Quotations Sent", value: int(q.sent), icon: <FiSend />, color: "#4f46e5" },
            { label: "Quotations Pending", value: int(q.pending), icon: <FiClock />, color: "#d97706" },
            { label: "Quotations Approved", value: int(q.approved), icon: <FiCheckCircle />, color: "#16a34a" },
            { label: "Quotes on Models We Don't Have", value: int(k.quotesOnModelsWeDontHave), icon: <FiAlertTriangle />, color: "#dc2626" },
          ]
        : [],
    },
    {
      title: "Sales & Revenue",
      gridClass: "ad-grid-4",
      cards: k
        ? [
            { label: "Close Rate", value: pct(k.closeRate), icon: <FiPercent />, color: "#7c3aed" },
            { label: "Total Revenue", value: usd(k.totalRevenue), icon: <FiDollarSign />, color: "#16a34a" },
            { label: "Total Units Sold", value: int(k.totalUnitsSold), icon: <FiPackage />, color: "#0284c7" },
            { label: "Average Order Value", value: usd2(k.averageOrderValue), icon: <FiShoppingCart />, color: "#4f46e5" },
          ]
        : [],
    },
  ];

  const statusData = k
    ? [
        { name: "Approved", value: num(q.approved), color: "#10b981", share: approvedShare },
        { name: "Pending", value: num(q.pending), color: "#f59e0b", share: pendingShare },
        { name: "Not sent yet", value: num(q.notSentYet), color: "#38bdf8", share: notSentShare },
      ].filter((d) => d.value > 0)
    : [];

  const moneyData = k
    ? [
        { name: "Revenue", value: num(k.totalRevenue), color: "#004aad" },
        { name: "Gross Profit", value: num(k.grossProfit), color: "#10b981" },
        { name: "Cost", value: Math.max(num(k.totalRevenue) - num(k.grossProfit), 0), color: "#cbd5e1" },
      ]
    : [];

  return (
    <div className="ad-shell">
      <div className="ad-header">
        <CommonHeader className="mb-0" recordInputLabel="Records" rightContent={<PageLogoutButton />} />
      </div>

      <div className="ad-scroll">
        <div className="ad-wrap">
          {/* Dashboard Header (REMAINS EXACTLY AS IS) */}
          <div className="ad-head">
            <div className="ad-brand">
              <h1>ATE Dashboard</h1>
              <p>
                Sales performance overview
                {data?.range?.start && data?.range?.end ? ` · ${data.range.start} → ${data.range.end}` : ""}
              </p>
            </div>
            <div className="ad-filters">
              <div className="ad-date-pill">
                <FiCalendar className="ad-date-icon" />
                <Select
                  value={period}
                  onChange={setPeriod}
                  className="ad-period-select"
                  bordered={false}
                  options={PERIODS}
                  suffixIcon={<FiChevronDown className="ad-arrow-icon" />}
                />
              </div>
              {period === "custom" && (
                <DatePicker.RangePicker onChange={(_, s) => setCustomRange(s[0] && s[1] ? [s[0], s[1]] : null)} />
              )}
              <button className="ad-refresh-btn" onClick={() => setReload((n) => n + 1)} title="Refresh">
                <FiRefreshCw className={loading ? "spin" : ""} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {error && <div className="ad-error">Could not load dashboard data. Please try again.</div>}
          {period === "custom" && !customRange && !data && <div className="ad-empty">Pick a date range to load data.</div>}

          {/* 3 Top Hero Metric Cards (REMAINS EXACTLY AS IS) */}
          <div className="ad-hero-cards">
            {loading && !k ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div className="ad-hero-card" key={i}>
                  <Skeleton active paragraph={{ rows: 2 }} title={false} />
                </div>
              ))
            ) : k ? (
              <>
                <div className="ad-hero-card">
                  <div className="ad-hero-info">
                    <span className="ad-hero-label">Total Revenue</span>
                    <b className="ad-hero-val text-blue">{usd(k.totalRevenue)}</b>
                    <span className="ad-hero-sub">
                      Avg order: <b>{usd2(k.averageOrderValue)}</b>
                    </span>
                  </div>
                  <div className="ad-hero-icon bg-blue-soft text-blue">
                    <FiDollarSign />
                  </div>
                </div>

                <div className="ad-hero-card">
                  <div className="ad-hero-info">
                    <span className="ad-hero-label">Close Rate</span>
                    <b className="ad-hero-val text-green">{pct(k.closeRate)}</b>
                    <span className="ad-hero-sub">
                      From <b>{int(k.totalLeads)}</b> total leads
                    </span>
                  </div>
                  <div className="ad-hero-icon bg-green-soft text-green">
                    <FiPercent />
                  </div>
                </div>

                <div className="ad-hero-card">
                  <div className="ad-hero-info">
                    <span className="ad-hero-label">Units Sold</span>
                    <b className="ad-hero-val text-blue">{int(k.totalUnitsSold)}</b>
                    <span className="ad-hero-sub">
                      <b>{int(k.totalUnitsSold)}</b> items in this period
                    </span>
                  </div>
                  <div className="ad-hero-icon bg-blue-soft text-blue">
                    <FiPackage />
                  </div>
                </div>
              </>
            ) : null}
          </div>

          {/* Quotation Pipeline (Updated to match Image 2 chevron flow) */}
          {k && (
            <div className="ad-pipe-box">
              <div className="ad-pipe-head">
                <div className="ad-pipe-title">
                  <FiLayers className="ad-pipe-icon" />
                  <h3>Quotation Pipeline</h3>
                </div>
                <span className="ad-pipe-count"><b>{int(totalQuotes)}</b> quotes in this period</span>
              </div>
              <div className="ad-chevron-pipeline">
                <div className="ad-chevron-stage stage-approved">
                  <div className="ad-chevron-badge bg-green">
                    <FiFileText />
                  </div>
                  <div className="ad-chevron-content">
                    <span className="ad-chevron-lbl">Approved</span>
                    <div className="ad-chevron-val">
                      <b>{int(q.approved)}</b> <small>({pct(approvedShare, 1)})</small>
                    </div>
                  </div>
                </div>

                <div className="ad-chevron-stage stage-pending">
                  <div className="ad-chevron-badge bg-amber">
                    <FiClock />
                  </div>
                  <div className="ad-chevron-content">
                    <span className="ad-chevron-lbl">Pending</span>
                    <div className="ad-chevron-val">
                      <b>{int(q.pending)}</b> <small>({pct(pendingShare, 1)})</small>
                    </div>
                  </div>
                </div>

                <div className="ad-chevron-stage stage-notsent">
                  <div className="ad-chevron-badge bg-blue">
                    <FiMail />
                  </div>
                  <div className="ad-chevron-content">
                    <span className="ad-chevron-lbl">Not sent yet</span>
                    <div className="ad-chevron-val">
                      <b>{int(q.notSentYet)}</b> <small>({pct(notSentShare, 1)})</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Key Performance Indicators (9 cards in 2 groups: 5 Leads & Quotations, 4 Sales & Revenue) */}
          <div className="ad-kpi-section">
            <div className="ad-kpi-sec-head">Key Performance Indicators</div>
            {loading && !k ? (
              <>
                <div className="ad-kpi-group">
                  <div className="ad-kpi-group-title">Leads & Quotations</div>
                  <div className="ad-kpi-grid ad-grid-5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div className="ad-kpi-item" key={i}>
                        <Skeleton active paragraph={{ rows: 1 }} title={false} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="ad-kpi-group">
                  <div className="ad-kpi-group-title">Sales & Revenue</div>
                  <div className="ad-kpi-grid ad-grid-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div className="ad-kpi-item" key={i}>
                        <Skeleton active paragraph={{ rows: 1 }} title={false} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              kpiGroups.map((grp) => (
                <div className="ad-kpi-group" key={grp.title}>
                  <div className="ad-kpi-group-title">{grp.title}</div>
                  <div className={`ad-kpi-grid ${grp.gridClass}`}>
                    {grp.cards.map((c) => (
                      <div className="ad-kpi-item" key={c.label}>
                        <div className="ad-kpi-circle-badge" style={{ background: c.color }}>
                          {c.icon}
                        </div>
                        <div className="ad-kpi-info">
                          <span className="ad-kpi-label" title={c.label}>{c.label}</span>
                          <div className="ad-kpi-val">{c.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Bottom Analytics Section (3 panels matching Image 2) */}
          {k && (
            <div className="ad-charts">
              {/* 1. Performance at a Glance (Matching Image 2) */}
              <div className="ad-panel">
                <div className="ad-panel-head">
                  <FiBarChart2 className="ad-panel-head-icon" />
                  <h3>Performance at a Glance</h3>
                </div>
                <div className="ad-glance-rows">
                  <div className="ad-glance-row">
                    <div className="ad-glance-top">
                      <div className="ad-glance-left">
                        <div className="ad-glance-icon-circle bg-blue">
                          <FiTarget />
                        </div>
                        <span className="ad-glance-title">Close Rate</span>
                      </div>
                      <b className="ad-glance-rate text-blue">{pct(k.closeRate, 2)}</b>
                    </div>
                    <div className="ad-glance-track">
                      <div style={{ width: `${Math.min(Math.max(k.closeRate, 0), 100)}%`, background: "#004aad" }} />
                    </div>
                    <small className="ad-glance-sub">Deals closed vs leads</small>
                  </div>

                  <div className="ad-glance-row">
                    <div className="ad-glance-top">
                      <div className="ad-glance-left">
                        <div className="ad-glance-icon-circle bg-green">
                          <FiCheckCircle />
                        </div>
                        <span className="ad-glance-title">Approval Share</span>
                      </div>
                      <b className="ad-glance-rate text-green">{pct(approvedShare, 1)}</b>
                    </div>
                    <div className="ad-glance-track">
                      <div style={{ width: `${Math.min(Math.max(approvedShare, 0), 100)}%`, background: "#16a34a" }} />
                    </div>
                    <small className="ad-glance-sub">Approved quotes vs all quotes</small>
                  </div>
                </div>
              </div>

              {/* 2. Quotation Status (Donut chart matching Image 2) */}
              <div className="ad-panel">
                <div className="ad-panel-head">
                  <FiFileText className="ad-panel-head-icon" />
                  <h3>Quotation Status</h3>
                </div>
                {statusData.length ? (
                  <div className="ad-donut">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={statusData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius="60%"
                          outerRadius="82%"
                          paddingAngle={3}
                          stroke="none"
                        >
                          {statusData.map((d) => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip formatter={(v) => int(v)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="ad-center">
                      <b>{int(totalQuotes)}</b>
                      <span>total quotes</span>
                    </div>
                  </div>
                ) : (
                  <div className="ad-empty">No quotations in this period.</div>
                )}
                <div className="ad-legend">
                  {statusData.map((d) => (
                    <span key={d.name}>
                      <i style={{ background: d.color }} />
                      {d.name} {int(d.value)} ({pct(d.share, 1)})
                    </span>
                  ))}
                </div>
              </div>

              {/* 3. Revenue vs Profit (Bar chart with top labels matching Image 2) */}
              <div className="ad-panel">
                <div className="ad-panel-head">
                  <FiBarChart2 className="ad-panel-head-icon" />
                  <h3>Revenue vs Profit</h3>
                </div>
                <div className="ad-chartbox">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={moneyData} margin={{ top: 28, left: 0, right: 10, bottom: 0 }}>
                      <CartesianGrid vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" stroke="#64748b" fontSize={12} tickLine={false} axisLine={false} />
                      <YAxis tickFormatter={(v) => `$${compact(v)}`} stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} width={56} />
                      <Tooltip formatter={(v) => usd(v)} cursor={false} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={52}>
                        <LabelList
                          dataKey="value"
                          position="top"
                          formatter={(v) => `$${compact(v)}`}
                          fill="#0f172a"
                          fontSize={12}
                          fontWeight={800}
                          offset={8}
                        />
                        {moneyData.map((d) => <Cell key={d.name} fill={d.color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AteDashboard;
